// JR-MANUAL-HOLDINGS M2 (cycle 244) — manager screen over the M1 endpoints.
// Off-platform holdings (SAFT / external cash / custodial) that feed
// gross_nav = positions + manuals. Append-only: edits are SUPERSESSIONS, and
// the ?history=1 chain is the dated value history James asked for. The
// supersede form LOCKS (asset, kind) — the backend 409s a key-change onto
// another live row; changing identity means creating a new holding.
// Attachment upload is deliberately deferred (M1 endpoints don't accept it).
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { PageHeader } from '../../components/shell';
import { fmtBase, fmtQty, fmtUnitPriceOps } from '../../lib/format';
import { api } from '../../lib/api';

interface Holding {
  id: string;
  instrument_kind: string;
  asset_symbol: string;
  quantity: string;
  manual_cost_basis_base: string;
  manual_price: string | null;
  price_as_of: string | null;
  base_currency: string;
  notes: string;
  effective_from: string;
  effective_to: string | null;
  created_by: string | null;
  created_at: string;
  superseded_by: string | null;
}

const KINDS = [
  ['saft', 'SAFT'],
  ['external_cash', 'External cash'],
  ['custodial_position', 'Custodial position'],
  ['other', 'Other'],
] as const;

const kindLabel = (k: string) => KINDS.find(([v]) => v === k)?.[1] ?? k;

const day = (iso: string | null) => (iso ? iso.slice(0, 10) : '—');

function staleness(priceAsOf: string | null): string | null {
  if (!priceAsOf) return null;
  const days = Math.floor((Date.now() - new Date(priceAsOf).getTime()) / 86400000);
  return days > 30 ? `${days}D OLD` : null;
}

interface FormState {
  instrument_kind: string;
  asset_symbol: string;
  quantity: string;
  manual_cost_basis_base: string;
  manual_price: string;
  price_as_of: string;
  base_currency: string;
  effective_from: string;
  effective_to: string;
  notes: string;
  reason: string;
}

const emptyForm = (): FormState => ({
  instrument_kind: 'saft', asset_symbol: '', quantity: '',
  manual_cost_basis_base: '', manual_price: '', price_as_of: '',
  base_currency: 'USD', effective_from: new Date().toISOString().slice(0, 10),
  effective_to: '', notes: '', reason: '',
});

function HoldingForm({ mode, initial, onSubmit, onCancel, busy, error }: {
  mode: 'create' | 'supersede';
  initial: FormState;
  onSubmit: (f: FormState) => void;
  onCancel: () => void;
  busy: boolean;
  error: string | null;
}) {
  const [f, setF] = useState<FormState>(initial);
  const set = (k: keyof FormState) => (e: any) => setF(p => ({ ...p, [k]: e.target.value }));
  const locked = mode === 'supersede';
  return (
    <form
      onSubmit={e => { e.preventDefault(); onSubmit(f); }}
      style={{ borderBottom: 'var(--hairline)', padding: '20px 0', marginTop: 12, display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}
    >
      <div className="field">
        <label className="field-label">KIND</label>
        {locked
          ? (
            <div>
              <div style={{ padding: '10px 0', borderBottom: 'var(--hairline)', color: 'var(--ink-3)' }}>{kindLabel(f.instrument_kind)}</div>
              <div className="label-s" style={{ marginTop: 4, color: 'var(--ink-3)' }}>LOCKED · IDENTITY IS A NEW HOLDING</div>
            </div>
          )
          : (
            <select className="field-input" value={f.instrument_kind} onChange={set('instrument_kind')}>
              {KINDS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          )}
      </div>
      <div className="field">
        <label className="field-label">ASSET</label>
        {locked
          ? (
            <div>
              <div style={{ padding: '10px 0', borderBottom: 'var(--hairline)', color: 'var(--ink-3)' }}>{f.asset_symbol}</div>
              <div className="label-s" style={{ marginTop: 4, color: 'var(--ink-3)' }}>LOCKED · IDENTITY IS A NEW HOLDING</div>
            </div>
          )
          : <input className="field-input" required maxLength={32} value={f.asset_symbol} onChange={set('asset_symbol')} placeholder="ACME-SAFT" />}
      </div>
      <div className="field">
        <label className="field-label">QUANTITY</label>
        <input className="field-input num" required type="number" step="any" min="0" value={f.quantity} onChange={set('quantity')} />
      </div>
      <div className="field">
        <label className="field-label">COST BASIS ({f.base_currency})</label>
        <input className="field-input num" required type="number" step="any" min="0" value={f.manual_cost_basis_base} onChange={set('manual_cost_basis_base')} />
      </div>
      <div className="field">
        <label className="field-label">MANUAL PRICE (optional)</label>
        <input className="field-input num" type="number" step="any" min="0" value={f.manual_price} onChange={set('manual_price')} placeholder="blank = pricing waterfall" />
      </div>
      <div className="field">
        <label className="field-label">PRICE AS OF</label>
        <input className="field-input" type="date" value={f.price_as_of} onChange={set('price_as_of')} />
      </div>
      <div className="field">
        <label className="field-label">EFFECTIVE FROM</label>
        <input className="field-input" required type="date" value={f.effective_from} onChange={set('effective_from')} />
      </div>
      <div className="field">
        <label className="field-label">EFFECTIVE TO (optional)</label>
        <input className="field-input" type="date" value={f.effective_to} onChange={set('effective_to')} />
      </div>
      <div className="field" style={{ gridColumn: '1 / 3' }}>
        <label className="field-label">NOTES</label>
        <input className="field-input" value={f.notes} onChange={set('notes')} placeholder="Wire ref, agreement date…" />
      </div>
      {locked && (
        <div className="field" style={{ gridColumn: '3 / 5' }}>
          <label className="field-label">REASON (required)</label>
          <input className="field-input" required value={f.reason} onChange={set('reason')} placeholder="Why this row changes" />
        </div>
      )}
      {error && (
        <p className="serif-body" style={{ gridColumn: '1 / 5', margin: 0, color: 'var(--oxblood)' }}>{error}</p>
      )}
      <div style={{ gridColumn: '1 / 5', display: 'flex', gap: 20, alignItems: 'center' }}>
        <button type="submit" className="btn" disabled={busy}>
          {busy ? 'Saving…' : mode === 'create' ? 'Add holding' : 'Supersede'}
        </button>
        <button type="button" className="btn-link" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}

export default function FundHoldings() {
  const { fundId } = useParams<{ fundId: string }>();
  const [holdings, setHoldings] = useState<Holding[] | null>(null);
  const [history, setHistory] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [formErr, setFormErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [creating, setCreating] = useState(false);
  const [superseding, setSuperseding] = useState<Holding | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!fundId) return;
    let cancelled = false;
    setErr(null);
    api<{ holdings: Holding[] }>(`/ops/funds/${fundId}/manual-holdings${history ? '?history=1' : ''}`)
      .then(d => { if (!cancelled) setHoldings(d.holdings); })
      .catch(() => { if (!cancelled) setErr('Failed to load holdings.'); });
    return () => { cancelled = true; };
  }, [fundId, history, reloadKey]);

  if (!fundId) return null;

  const submit = async (f: FormState) => {
    setBusy(true);
    setFormErr(null);
    const body: Record<string, unknown> = {
      instrument_kind: f.instrument_kind,
      asset_symbol: f.asset_symbol.trim(),
      quantity: f.quantity,
      manual_cost_basis_base: f.manual_cost_basis_base,
      base_currency: f.base_currency,
      notes: f.notes,
      effective_from: f.effective_from ? `${f.effective_from}T00:00:00Z` : '',
    };
    if (f.manual_price) body.manual_price = f.manual_price;
    if (f.price_as_of) body.price_as_of = `${f.price_as_of}T00:00:00Z`;
    if (f.effective_to) body.effective_to = `${f.effective_to}T23:59:59Z`;
    if (superseding) body.reason = f.reason;
    try {
      const path = superseding
        ? `/ops/funds/${fundId}/manual-holdings/${superseding.id}/supersede`
        : `/ops/funds/${fundId}/manual-holdings`;
      await api(path, { method: 'POST', body });
      setCreating(false);
      setSuperseding(null);
      setReloadKey(k => k + 1);
    } catch (e: any) {
      // surface the backend's precise 409/400 reasons verbatim (duplicate
      // live key → supersede pointer; wound-down row; raced supersede)
      setFormErr(e?.payload?.detail || 'Could not save. Please retry.');
    } finally {
      setBusy(false);
    }
  };

  const live = (h: Holding) => !h.superseded_by;

  return (
    <>
      <PageHeader kicker="FUND" title={<>Manual <em>holdings.</em></>} />
      <p className="serif-body" style={{ marginTop: 4, maxWidth: '80ch', color: 'var(--ink-3)' }}>
        Off-platform positions — SAFTs, externally-held cash, custodial lines — that
        count toward the fund's gross NAV alongside tracked accounts. Rows are
        append-only: every change supersedes the prior row, and the chain is the
        holding's dated history. These are a different book from the treasury
        page's "outside of TRES" estimates, which are observability figures and
        never feed NAV.
      </p>
      {err && <p className="serif-body" style={{ color: 'var(--oxblood)', marginTop: 12 }}>{err}</p>}

      <section style={{ marginTop: 24 }}>
        <div className="kicker-row">
          <span className="kicker">{history ? 'HOLDINGS · FULL HISTORY' : 'HOLDINGS · LIVE'}</span>
          <span style={{ display: 'flex', gap: 20 }}>
            <button type="button" className="btn-link" onClick={() => setHistory(v => !v)}>
              {history ? 'Live only' : 'Show history'}
            </button>
            <button type="button" className="btn-link" onClick={() => { setCreating(v => !v); setSuperseding(null); setFormErr(null); }}>
              {creating ? '− Cancel' : '+ Add holding'}
            </button>
          </span>
        </div>

        {creating && (
          <HoldingForm mode="create" initial={emptyForm()} onSubmit={submit}
                       onCancel={() => { setCreating(false); setFormErr(null); }}
                       busy={busy} error={formErr} />
        )}
        {superseding && (
          <HoldingForm
            mode="supersede"
            initial={{
              ...emptyForm(),
              instrument_kind: superseding.instrument_kind,
              asset_symbol: superseding.asset_symbol,
              quantity: superseding.quantity,
              manual_cost_basis_base: superseding.manual_cost_basis_base,
              manual_price: superseding.manual_price ?? '',
              price_as_of: superseding.price_as_of ? superseding.price_as_of.slice(0, 10) : '',
              base_currency: superseding.base_currency,
              notes: '',
            }}
            onSubmit={submit}
            onCancel={() => { setSuperseding(null); setFormErr(null); }}
            busy={busy} error={formErr}
          />
        )}

        <table className="tbl" style={{ marginTop: 12 }}>
          <thead>
            <tr>
              <th>KIND</th><th>ASSET</th>
              <th data-align="right">QUANTITY</th>
              <th data-align="right">COST BASIS</th>
              <th data-align="right">PRICE</th>
              <th>EFFECTIVE</th>
              {history && <th>STATUS</th>}
              <th data-align="right"></th>
            </tr>
          </thead>
          <tbody>
            {holdings === null && [0, 1, 2].map(i => (
              <tr key={`sk${i}`}>
                <td colSpan={history ? 8 : 7} style={{ padding: '16px 12px', borderBottom: 'var(--hairline)' }}>
                  <div style={{ height: 10, maxWidth: i === 1 ? '55%' : '70%', background: 'var(--rule-soft)' }} />
                </td>
              </tr>
            ))}
            {(holdings || []).map(h => (
              <tr key={h.id} style={live(h) ? undefined : { color: 'var(--ink-3)' }}>
                <td className="label-s">{kindLabel(h.instrument_kind).toUpperCase()}</td>
                <td>{h.asset_symbol}</td>
                <td data-align="right"><span className="num">{fmtQty(Number(h.quantity))}</span></td>
                <td data-align="right"><span className="num">{fmtBase(Number(h.manual_cost_basis_base), h.base_currency as any)}</span></td>
                <td data-align="right">
                  {h.manual_price !== null ? (
                    <>
                      {/* unit MARK, not a money total — 6dp ops formatter
                          (hard rule; a $0.0125 SAFT must not render $0.01) */}
                      <span className="num">{fmtUnitPriceOps(Number(h.manual_price), h.base_currency)}</span>
                      {staleness(h.price_as_of) && (
                        <span className="label-s" style={{ display: 'block', color: 'var(--oxblood)' }}>{staleness(h.price_as_of)}</span>
                      )}
                    </>
                  ) : (
                    <span className="label-s" style={{ color: 'var(--ink-3)' }}>WATERFALL</span>
                  )}
                </td>
                <td className="num" style={{ fontSize: 12, color: 'var(--ink-3)' }}>
                  {day(h.effective_from)}
                  {(() => {
                    // a superseded row's range TERMINATES at its successor's
                    // effective_from — two open-ended generations would
                    // contradict the "dated history" promise
                    const successor = h.superseded_by
                      ? (holdings || []).find(x => x.id === h.superseded_by)
                      : null;
                    if (successor) return ` → ${day(successor.effective_from)}`;
                    if (h.effective_to) return ` → ${day(h.effective_to)}`;
                    return live(h) ? ' →' : '';
                  })()}
                </td>
                {history && (
                  <td className="label-s" style={{ color: live(h) ? 'var(--ink-2)' : 'var(--ink-3)' }}>
                    {live(h) ? 'LIVE' : 'SUPERSEDED'}
                  </td>
                )}
                <td data-align="right">
                  {live(h) && (
                    <button type="button" className="btn-link"
                            onClick={() => { setSuperseding(h); setCreating(false); setFormErr(null); window.scrollTo({ top: 0 }); }}>
                      Supersede
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {holdings && !holdings.length && (
              <tr><td colSpan={history ? 8 : 7} style={{ padding: '32px 12px', textAlign: 'center', fontFamily: 'var(--serif)', fontStyle: 'italic', color: 'var(--ink-3)' }}>
                No manual holdings recorded for this fund.
              </td></tr>
            )}
          </tbody>
        </table>
      </section>
    </>
  );
}
